import { InputGroup } from '@/components/ui/input-group';
import { EMAIL_COMPOSER_ROW_LABEL_WIDTH } from '@/components/email-composer/email-composer-layout';
import { useDebounce } from '@/hooks/useDebounce';
import { useSearchAffiliatesQuery } from '@/hooks/queries/use-search-affiliates-query';
import type { RecipientData } from '@/stores/affiliate-bulk-email-store';
import useAffiliateBulkEmailStore from '@/stores/affiliate-bulk-email-store';
import {
  Avatar,
  AvatarGroup,
  Box,
  createListCollection,
  Flex,
  Highlight,
  HStack,
  Icon,
  Input,
  Listbox,
  Popover,
  Separator,
  Tag,
  Text,
} from '@chakra-ui/react';
import type { AffiliateUserListItem } from '@fluenceur/types';
import { NavArrowDown, Search } from 'iconoir-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ── Constants ────────────────────────────────────────────

const MAX_VISIBLE_TAGS = 6;
const MAX_VISIBLE_AVATARS = 3;

const NAME_HIGHLIGHT_STYLES = {
  fontWeight: 'bold',
  color: 'primary.fg',
  bg: 'transparent',
} as const;
const EMAIL_HIGHLIGHT_STYLES = {
  fontWeight: 'semibold',
  color: 'primary.fg',
  bg: 'transparent',
} as const;

// ── Sub-components ───────────────────────────────────────

interface AffiliateListItemProps {
  affiliate: AffiliateUserListItem;
  item: { value: string; label: string };
  dimmed?: boolean;
  query?: string;
}

function AffiliateListItem({ affiliate, item, dimmed, query }: AffiliateListItemProps) {
  return (
    <Listbox.Item
      item={item}
      px={2}
      py={1}
      rounded="md"
      cursor="pointer"
      opacity={dimmed ? 0.5 : 1}
      minH="auto"
      {...(dimmed && {
        bg: 'transparent',
        _hover: { bg: 'transparent' },
        _selected: { bg: 'transparent' },
      })}
    >
      <HStack gap={2} w="full">
        <Avatar.Root size="2xs">
          {affiliate.avatar?.fileUrl && <Avatar.Image src={affiliate.avatar.fileUrl} />}
          <Avatar.Fallback>{affiliate.name.charAt(0)}</Avatar.Fallback>
        </Avatar.Root>
        <Box flex={1} minW={0} lineHeight="1.3">
          <Text fontSize="xs" fontWeight="medium" truncate>
            {query ? (
              <Highlight query={query} styles={NAME_HIGHLIGHT_STYLES}>
                {affiliate.name}
              </Highlight>
            ) : (
              affiliate.name
            )}
          </Text>
          {!dimmed && (
            <Text fontSize="2xs" color="fg.muted" truncate>
              {query ? (
                <Highlight query={query} styles={EMAIL_HIGHLIGHT_STYLES}>
                  {affiliate.email}
                </Highlight>
              ) : (
                affiliate.email
              )}
            </Text>
          )}
        </Box>
      </HStack>
      <Listbox.ItemIndicator />
    </Listbox.Item>
  );
}

interface RecipientTagsProps {
  recipients: RecipientData[];
  onRemove: (id: string) => void;
}

function RecipientTags({ recipients, onRemove }: RecipientTagsProps) {
  const { t } = useTranslation();

  if (recipients.length === 0) {
    return (
      <Text fontSize="sm" color="fg.subtle">
        {t('bulkEmail.compose.toSearch')}
      </Text>
    );
  }

  if (recipients.length > MAX_VISIBLE_TAGS) {
    return (
      <HStack gap={2} flexShrink={0}>
        <AvatarGroup size="2xs" gap="0" spaceX="-1.5">
          {recipients.slice(0, MAX_VISIBLE_AVATARS).map((r) => (
            <Avatar.Root key={r.id}>
              {r.avatarUrl && <Avatar.Image src={r.avatarUrl} />}
              <Avatar.Fallback name={r.name} />
            </Avatar.Root>
          ))}
        </AvatarGroup>
        <Text fontSize="sm" fontWeight="medium">
          {t('bulkEmail.compose.affiliates', { count: recipients.length })}
        </Text>
      </HStack>
    );
  }

  return (
    <>
      {recipients.map((r) => (
        <Tag.Root key={r.id} size="sm" rounded="full" variant="subtle" colorPalette="gray">
          <Tag.Label>{r.name}</Tag.Label>
          <Tag.EndElement>
            <Tag.CloseTrigger
              onClick={(e) => {
                e.stopPropagation();
                onRemove(r.id);
              }}
            />
          </Tag.EndElement>
        </Tag.Root>
      ))}
    </>
  );
}

// ── Main component ───────────────────────────────────────

interface RecipientAutocompleteProps {
  variant?: 'card' | 'row';
}

function RecipientAutocomplete({ variant = 'card' }: RecipientAutocompleteProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const recipients = useAffiliateBulkEmailStore((s) => s.recipients);
  const selectedIds = useAffiliateBulkEmailStore((s) => s.selectedIds);
  const addRecipient = useAffiliateBulkEmailStore((s) => s.addRecipient);
  const removeRecipient = useAffiliateBulkEmailStore((s) => s.removeRecipient);

  const { data } = useSearchAffiliatesQuery({ perPage: 50, search: debouncedSearch || undefined });
  const allAffiliates = useMemo(() => data?.data ?? [], [data?.data]);

  const { available, added } = useMemo(() => {
    const avail: AffiliateUserListItem[] = [];
    const add: AffiliateUserListItem[] = [];
    for (const a of allAffiliates) {
      (selectedIds.has(a.id) ? add : avail).push(a);
    }
    return { available: avail, added: add };
  }, [allAffiliates, selectedIds]);

  const collection = useMemo(
    () =>
      createListCollection({
        items: [...available, ...added].map((a) => ({
          value: a.id,
          label: a.name,
        })),
      }),
    [available, added]
  );

  const affiliateById = useMemo(
    () => new Map(allAffiliates.map((a) => [a.id, a])),
    [allAffiliates]
  );

  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      const store = useAffiliateBulkEmailStore.getState();
      const currentIds = store.selectedIds;
      const newIds = new Set(details.value);
      for (const id of newIds) {
        if (!currentIds.has(id)) {
          const aff = affiliateById.get(id);
          if (aff) {
            addRecipient({
              id: aff.id,
              name: aff.name,
              email: aff.email,
              avatarUrl: aff.avatar?.fileUrl,
              programs: aff.programs.map((p) => ({ id: p.id, name: p.name })),
            });
          }
        }
      }
      for (const id of currentIds) {
        if (!newIds.has(id)) {
          removeRecipient(id);
        }
      }
    },
    [affiliateById, addRecipient, removeRecipient]
  );

  const selectedIdArray = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const recipientList = useMemo(() => Array.from(recipients.values()), [recipients]);
  const isRow = variant === 'row';

  return (
    <Popover.Root positioning={{ placement: 'bottom-start' }}>
      <Popover.Trigger asChild>
        <Box
          px={isRow ? 0 : 3}
          py={isRow ? 0 : 2.5}
          rounded={isRow ? 'none' : 'lg'}
          border={isRow ? 'none' : '1px solid'}
          borderColor="border"
          bg={isRow ? 'transparent' : 'bg'}
          cursor="pointer"
          _hover={{ borderColor: 'border.emphasized' }}
          transition="border-color 0.15s"
        >
          <Flex align="center" gap={2} flexWrap="wrap" minH={isRow ? '28px' : undefined}>
            <Text
              fontSize="sm"
              color="fg.muted"
              w={isRow ? EMAIL_COMPOSER_ROW_LABEL_WIDTH : undefined}
              flexShrink={0}
            >
              {t('bulkEmail.compose.to')}:
            </Text>
            <RecipientTags recipients={recipientList} onRemove={removeRecipient} />
            <Icon color="fg.muted" asChild boxSize={4} ml="auto" flexShrink={0}>
              <NavArrowDown />
            </Icon>
          </Flex>
        </Box>
      </Popover.Trigger>

      <Popover.Positioner>
        <Popover.Content w="360px" p={0} rounded="lg" shadow="lg" overflow="hidden">
          <Box pl={1} pr={3} pt={2.5} pb={1.5}>
            <InputGroup w="full" startElement={<Search width={14} height={14} />} startOffset="0px">
              <Input
                size="sm"
                placeholder={t('bulkEmail.compose.toSearch')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="flushed"
                border="none"
                outline="none"
                _focus={{
                  boxShadow: 'none',
                  outline: 'none',
                  ring: 'none',
                  borderColor: 'transparent',
                }}
              />
            </InputGroup>
          </Box>

          <Separator />

          <Listbox.Root
            collection={collection}
            selectionMode="multiple"
            value={selectedIdArray}
            onValueChange={handleValueChange}
          >
            <Listbox.Content
              maxH="280px"
              overflowY="auto"
              px={1}
              py={1.5}
              border="none"
              shadow="none"
            >
              {available.length === 0 && added.length === 0 ? (
                <Text fontSize="xs" color="fg.muted" px={3} py={4} textAlign="center">
                  {t('bulkEmail.compose.toNoResults')}
                </Text>
              ) : (
                <>
                  {available.length > 0 && (
                    <Listbox.ItemGroup>
                      {available.map((a) => {
                        const item = collection.find(a.id);
                        if (!item) return null;
                        return (
                          <AffiliateListItem key={a.id} affiliate={a} item={item} query={search} />
                        );
                      })}
                    </Listbox.ItemGroup>
                  )}

                  {added.length > 0 && available.length > 0 && <Separator mt={0.5} />}

                  {added.length > 0 && (
                    <Listbox.ItemGroup>
                      <Listbox.ItemGroupLabel fontSize="2xs" color="fg.subtle" px={2.5} py={1}>
                        {t('bulkEmail.compose.toAdded')}
                      </Listbox.ItemGroupLabel>
                      {added.map((a) => {
                        const item = collection.find(a.id);
                        if (!item) return null;
                        return <AffiliateListItem key={a.id} affiliate={a} item={item} dimmed />;
                      })}
                    </Listbox.ItemGroup>
                  )}
                </>
              )}
            </Listbox.Content>
          </Listbox.Root>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}

export default RecipientAutocomplete;
